import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import {
  stroot,
  subscription as subscriptionAPI,
  onboarding as onboardingAPI,
} from '@birdi/js-sdk';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import PlanIcon from '@birdi/icons/onboarding-plan.svg';
import PlusPlanSVG from '@birdi/icons/plus-plan-2.svg';
import GrowthPlanSVG from '@birdi/icons/growth-plan-2.svg';
import InfoSVG from '@birdi/icons/onboarding-info.svg';
import { useMetaSelector } from '../../../meta/reducer';
import { ProgressBar } from '../progress-bar';
import * as styles from '../styles';
import { goToDashboard } from './organisation-setup';
import {
  PaymentIntervalOption,
  PlanData,
} from '../../../administration/plan-and-usage/change-plan/plan-cards/plan-option';
import { LoadingSpinner } from '@birdi/loading';

const IntervalTerminologyMapping = {
  annual: 'year',
  monthly: 'month',
};

interface SelectPlanProps {}

const SelectPlan: React.FC<SelectPlanProps> = () => {
  const theme = useTheme() as BirdiTheme;
  const navigate = useNavigate();
  const meta = useMetaSelector();

  // Prevent access if plan has already been selected/paid for
  useEffect(() => {
    // Check if user already has a subscription (plan already selected and paid)
    // @ts-ignore - stripeSubscriptionId exists at runtime but not in type definition
    if (meta.organisation?.stripeSubscriptionId) {
      // Determine which plan was selected based on product name
      const planName =
        meta.organisation?.stripeProductName?.toLowerCase() || '';
      const isPlusPlan = planName.includes('plus');
      const isGrowthPlan = planName.includes('growth');

      if (isPlusPlan) {
        // Plus plan: complete onboarding and go to dashboard
        goToDashboard(meta, navigate);
      } else if (isGrowthPlan) {
        // Growth plan: go to invite team page
        navigate('/onboarding/invite-your-team');
      } else {
        // Fallback: go to invite team page
        navigate('/onboarding/invite-your-team');
      }
    }
    // @ts-ignore - stripeSubscriptionId exists at runtime but not in type definition
  }, [
    meta.loaded,
    meta.organisation?.stripeSubscriptionId,
    meta.organisation?.stripeProductName,
    navigate,
    meta,
  ]);

  // Check for payment success on mount (when redirected back from Stripe)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentStatus = searchParams.get('payment_status');

    if (!paymentStatus) return; // No payment status, nothing to do

    // Remove payment_status from URL immediately
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('payment_status');
    window.history.replaceState({}, document.title, newUrl);

    if (paymentStatus === 'successful') {
      // Try to get plan type from organisation's stripeProductName first
      const planName =
        meta.organisation?.stripeProductName?.toLowerCase() || '';
      const isPlusPlan = planName.includes('plus');
      const isGrowthPlan = planName.includes('growth');

      // If organisation data isn't available yet, fall back to sessionStorage
      let planType: 'plus' | 'growth' | null = null;
      if (isPlusPlan) {
        planType = 'plus';
      } else if (isGrowthPlan) {
        planType = 'growth';
      } else {
        // Fallback to sessionStorage if organisation data not updated yet
        const storedPlanType = sessionStorage.getItem('selectedPlanType');
        if (storedPlanType === 'plus' || storedPlanType === 'growth') {
          planType = storedPlanType as 'plus' | 'growth';
        }
      }

      // Clear sessionStorage after using it
      sessionStorage.removeItem('selectedPlanType');

      // Route based on plan type
      if (planType === 'growth') {
        navigate('/onboarding/invite-your-team');
      } else if (planType === 'plus') {
        // Complete onboarding and go to dashboard if plus plan is selected
        goToDashboard(meta, navigate);
      }
      // If plan type cannot be determined, stay on this page
    } else if (paymentStatus === 'unsuccessful') {
      // Payment failed, stay on this page
      // Clear sessionStorage on failure
      sessionStorage.removeItem('selectedPlanType');
    }
  }, [navigate, meta.loaded, meta.organisation?.stripeProductName]);

  const [plans, setPlans] = useState<PlanData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] =
    useState<PaymentIntervalOption>('annual');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    subscriptionAPI
      .getDefaultPlanOptions(stroot('otii6o'))
      .then((res) => {
        const plansData = Array.isArray(res.body)
          ? res.body
          : res.body?.plans && Array.isArray(res.body.plans)
            ? res.body.plans
            : [];
        setPlans(plansData);
        const plusPlans = plansData.filter((p: PlanData) =>
          p.productName?.toLowerCase().includes('plus'),
        );
        const plusPlanWithAnnual = plusPlans.find((p: PlanData) =>
          p.prices?.some((price) => price.interval === 'year'),
        );
        if (plusPlanWithAnnual) {
          setSelectedPlan('plus');
          setSelectedInterval('annual');
        } else if (plusPlans.length > 0) {
          setSelectedPlan('plus');
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, []);

  const plusPlan = plans.find((plan) =>
    plan.productName?.toLowerCase().includes('plus'),
  );
  const growthPlan = plans.find((plan) =>
    plan.productName?.toLowerCase().includes('growth'),
  );

  const formatPrice = (
    price: number,
    currencyCode: string,
    interval: string,
  ) => {
    let priceInDollars = price / 100;
    if (interval === 'year') {
      priceInDollars = priceInDollars / 12;
    }
    const upperCurrency = currencyCode.toUpperCase();

    // Special formatting for AUD
    if (upperCurrency === 'AUD') {
      return `A$${priceInDollars.toFixed(2)}`;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: upperCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceInDollars);
  };

  const getPriceForPlan = (
    plan: PlanData | undefined,
    interval: PaymentIntervalOption,
  ) => {
    if (!plan) return 'Contact us';
    const priceOption = plan.prices?.find(
      (p) => p.interval === IntervalTerminologyMapping[interval],
    );
    if (priceOption) {
      const currencyCode = (
        priceOption.currency ||
        meta.organisation?.currency ||
        'usd'
      ).toUpperCase();
      const formattedPrice = formatPrice(
        priceOption.price,
        currencyCode,
        priceOption.interval,
      );

      // Add "incl GST" for AUD, currency code for others
      if (currencyCode === 'AUD') {
        return `${formattedPrice} incl GST`;
      }
      return `${formattedPrice} ${currencyCode}`;
    }
    return 'Contact us';
  };

  const handlePlanSelect = (planType: 'plus' | 'growth') => {
    setSelectedPlan(planType);
  };

  const handleContinue = async () => {
    if (!selectedPlan || isProcessing) return;
    setError('');

    const plan = selectedPlan === 'plus' ? plusPlan : growthPlan;
    if (!plan) {
      console.error('Plan data not found for selected plan:', selectedPlan, { plans });
      setError('Something went wrong. Please try again or contact support.');
      return;
    }

    // Growth plan only supports annual billing, Plus plan uses selected interval
    const intervalToUse =
      selectedPlan === 'growth' ? 'annual' : selectedInterval;

    const priceOption = plan.prices?.find(
      (p) => p.interval === IntervalTerminologyMapping[intervalToUse],
    );

    if (!priceOption?.id) {
      console.error('No price option found for interval:', intervalToUse, { plan });
      setError('Something went wrong. Please try again or contact support.');
      return;
    }

    try {
      setIsProcessing(true);

      // Store the selected plan type as backup before redirecting to Stripe
      // This will be used if organisation data isn't updated yet when we return
      sessionStorage.setItem('selectedPlanType', selectedPlan);

      const response = await subscriptionAPI.updatePlan(
        stroot('anhen7'),
        priceOption.id,
        false,
      );

      if (response.body.stripeSubscriptionId) {
        // Plan already set, route directly based on plan type
        if (selectedPlan === 'growth') {
          navigate('/onboarding/invite-your-team');
        } else if (selectedPlan === 'plus') {
          goToDashboard(meta, navigate);
        }
      } else if (response.body.redirectUrl) {
        window.location.href = response.body.redirectUrl;
      }
    } catch (err) {
      console.error('Error selecting plan:', err);
      setError('Something went wrong. Please try again or contact support.');
      setIsProcessing(false);
      // Clear sessionStorage on error
      sessionStorage.removeItem('selectedPlanType');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        `}
      >
        <LoadingSpinner color="#2287FA" size="50px" />
      </div>
    );
  }

  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <PlanIcon />
            </div>
            <h1 css={styles.title}>Select your plan</h1>
          </div>

          <ProgressBar currentStep={5} totalSteps={6} />

          <p css={styles.subtitle}>
            Start with a 7-day free trial – no charges today.
          </p>
        </div>
        {/* Plan Cards */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          `}
        >
          {/* Plus Plan */}
          <div
            css={(theme: BirdiTheme) => css`
              border: 2px solid
                ${selectedPlan === 'plus' ? theme.ocean : theme.mono20};
              border-radius: 0.75rem;
              padding: 1.5rem 1em;
              background-color: ${theme.mono0};
              cursor: pointer;
              transition: all 0.2s ease;
              position: relative;

              &:hover {
                border-color: ${theme.ocean};
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
              }

              ${selectedPlan === 'plus' &&
              css`
                box-shadow: 0 0 0 3px ${theme.ocean}20;
              `}
            `}
            onClick={() => handlePlanSelect('plus')}
          >
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                min-width: 0;
              `}
            >
              {/* Icon - Bigger and centered on left */}
              <div
                css={css`
                  width: 5rem;
                  height: 5rem;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  svg {
                    width: 100%;
                    height: 100%;
                  }
                `}
              >
                <PlusPlanSVG />
              </div>

              {/* Content */}
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                `}
              >
                {/* First row: Plan name and price */}
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    margin-bottom: 0.5rem;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                  `}
                >
                  <h3
                    css={(theme: BirdiTheme) => css`
                      font-size: 1.25rem;
                      font-weight: bold;
                      color: ${theme.mono70};
                      margin: 0;
                    `}
                  >
                    Plus
                  </h3>
                  <div
                    css={(theme: BirdiTheme) => css`
                      display: flex;
                      align-items: center;
                      white-space: nowrap;
                    `}
                  >
                    <span
                      css={(theme: BirdiTheme) => css`
                        font-size: 1rem;
                        font-weight: bold;
                        color: ${theme.mono70};
                      `}
                    >
                      {getPriceForPlan(plusPlan, selectedInterval)}
                    </span>
                    <span
                      css={(theme: BirdiTheme) => css`
                        font-size: 1rem;
                        font-weight: normal;
                        color: ${theme.mono70};
                        margin-left: 0.25rem;
                      `}
                    >
                      per month
                    </span>
                  </div>
                </div>

                {/* Second row: Interval toggle */}
                {plusPlan && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      background-color: #f0f0f0;
                      border-radius: 1.25em;
                      padding: 0.2em;
                      width: fit-content;
                      margin-bottom: 0.75rem;
                    `}
                  >
                    <button
                      css={(theme: BirdiTheme) => css`
                        padding: 0.4em 0.6em;
                        border-radius: 1em;
                        border: none;
                        font-weight: bold;
                        font-size: 0.8em;
                        cursor: pointer;
                        background-color: ${selectedInterval === 'annual'
                          ? theme.ocean
                          : 'transparent'};
                        color: ${selectedInterval === 'annual'
                          ? 'white'
                          : theme.mono70};
                        transition: all 0.2s ease;
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedInterval('annual');
                      }}
                    >
                      Annual
                    </button>
                    <button
                      css={(theme: BirdiTheme) => css`
                        padding: 0.4em 0.6em;
                        border-radius: 1em;
                        border: none;
                        font-weight: bold;
                        font-size: 0.8em;
                        cursor: pointer;
                        background-color: ${selectedInterval === 'monthly'
                          ? theme.ocean
                          : 'transparent'};
                        color: ${selectedInterval === 'monthly'
                          ? 'white'
                          : theme.mono70};
                        transition: all 0.2s ease;
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedInterval('monthly');
                      }}
                    >
                      Monthly
                    </button>
                  </div>
                )}

                {/* Description with inline link */}
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.875rem;
                    color: ${theme.mono60};
                    margin: 0;
                    line-height: 1.5;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                  `}
                >
                  Core visualization tools & map sharing{' '}
                  {/* – for solo professionals to centralize & streamline their geospatial processes.{' '} */}
                  <a
                    href="https://www.birdi.io/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    css={(theme: BirdiTheme) => css`
                      color: ${theme.deepOcean};
                      text-decoration: underline;
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    View included features
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Growth Plan */}
          <div
            css={(theme: BirdiTheme) => css`
              border: 2px solid
                ${selectedPlan === 'growth' ? theme.ocean : theme.mono20};
              border-radius: 0.75rem;
              padding: 1.5rem 1em;
              background-color: ${theme.mono0};
              cursor: pointer;
              transition: all 0.2s ease;
              position: relative;

              &:hover {
                border-color: ${theme.ocean};
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
              }

              ${selectedPlan === 'growth' &&
              css`
                box-shadow: 0 0 0 3px ${theme.ocean}20;
              `}
            `}
            onClick={() => handlePlanSelect('growth')}
          >
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                min-width: 0;
              `}
            >
              {/* Icon - Bigger and centered on left */}
              <div
                css={css`
                  width: 5rem;
                  height: 5rem;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  svg {
                    width: 100%;
                    height: 100%;
                  }
                `}
              >
                <GrowthPlanSVG />
              </div>

              {/* Content */}
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                `}
              >
                {/* First row: Plan name and price */}
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    margin-bottom: 0.5rem;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                  `}
                >
                  <h3
                    css={(theme: BirdiTheme) => css`
                      font-size: 1.25rem;
                      font-weight: bold;
                      color: ${theme.mono70};
                      margin: 0;
                    `}
                  >
                    Growth
                  </h3>
                  <div
                    css={(theme: BirdiTheme) => css`
                      display: flex;
                      align-items: center;
                      white-space: nowrap;
                    `}
                  >
                    <span
                      css={(theme: BirdiTheme) => css`
                        font-size: 1rem;
                        font-weight: bold;
                        color: ${theme.mono70};
                      `}
                    >
                      {getPriceForPlan(growthPlan, 'annual')}
                    </span>
                    <span
                      css={(theme: BirdiTheme) => css`
                        font-size: 1rem;
                        font-weight: normal;
                        color: ${theme.mono70};
                        margin-left: 0.25rem;
                      `}
                    >
                      per month
                    </span>
                  </div>
                </div>

                {/* Second row: Annual billing info */}
                <div
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.875rem;
                    color: ${theme.mono60};
                    margin-bottom: 0.75rem;
                  `}
                >
                  Annual billing only
                </div>

                {/* Description with inline link */}
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.875rem;
                    color: ${theme.mono60};
                    margin: 0;
                    line-height: 1.5;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                  `}
                >
                  Powerful visualization, collaboration & advanced outputs{' '}
                  {/* – for growing businesses ready to level up their geospatial workflows. */}
                  <a
                    href="https://www.birdi.io/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    css={(theme: BirdiTheme) => css`
                      color: ${theme.deepOcean};
                      text-decoration: underline;
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    View included features
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Information Box */}
        <div
          css={(theme: BirdiTheme) => css`
            background-color: ${theme.lightOcean};
            border-radius: 0.5rem;
            padding: 1rem;
            margin-top: 1rem;
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            flex-shrink: 0;
          `}
        >
          <div
            css={(theme: BirdiTheme) => css`
              width: 1.25rem;
              height: 1.25rem;
              min-width: 1.25rem;
              color: ${theme.ocean};
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <InfoSVG />
          </div>
          <div
            css={(theme: BirdiTheme) => css`
              font-size: 0.875rem;
              color: ${theme.deepOcean};
              margin: 0;
              line-height: 1.5;
            `}
          >
            <strong>Considering the Ultimate plan?</strong>
            <br />
            We offer a personalized onboarding experience to get you set up for
            success.
            <br />
            <a
              href="https://www.birdi.io/book-a-demo"
              target="_blank"
              rel="noopener noreferrer"
              css={(theme: BirdiTheme) => css`
                color: ${theme.deepOcean};
                text-decoration: underline;
                font-weight: bold;
              `}
            >
              Book a session with our team here.
            </a>
          </div>
        </div>

        {error && <div css={styles.errorMessage}>{error}</div>}

        {/* Buttons */}
        <div
          css={css`
            display: flex;
            gap: 0.75rem;
            margin-top: 1rem;
            flex-shrink: 0;
          `}
        >
          <button
            type="button"
            onClick={handleBack}
            css={styles.secondaryButton(theme)}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!selectedPlan || isProcessing || (!plusPlan && !growthPlan)}
            css={styles.primaryButton(theme)}
          >
            {isProcessing ? (
              <LoadingSpinner color="white" size="1em" />
            ) : (
              'Continue →'
            )}
          </button>
        </div>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default SelectPlan;
